import { AlertCircle, Calendar, Globe, User } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isValid, parse } from "date-fns";
import { useAnalytics } from "../hooks/useAnalytics";
import { useContactForm } from "../hooks/useContactForm";
import { getCountryByCode, isUnderEighteen } from "../utils/validationUtils";
import { fixDateFormatting } from "../utils/dateUtils";
import { useFieldErrorTracking } from "../utils/trackingUtils";
import {
  ContactFormSchema,
  ExistingPatientContactFormSchema,
  existingPatientContactFormSchema,
  NewPatientContactFormSchema,
} from "./contact-form/contactFormSchema";
import { QuestionnaireAnswers } from "./contact-form/utils/questionnaireStorage";
import {
  CanadianEthnicitySelector,
  EthnicitySelector,
} from "./contact-form/EthnicitySelector";
import { ConsentBodyText } from "./ConsentBodyText";
import { PhoneNumberInput } from "./PhoneNumberInput";
import { TermsAcceptance } from "./contact-form/TermsAcceptance";
import { Modal } from "./ui/Modal";
import { Controller as RHController } from "react-hook-form";
import { formatPostalCode } from "../utils/countryUtils";
import { getContactFormState, setContactFormState } from "./contact-form/utils/contactFormStorage";
import { TrialSiteWithConsent, useNearestTrialSite } from "../hooks/useNearestTrialSite";
import { TrialIdConfig } from "../types/landingPage";
import { getMatchingTrialId } from "./contact-form/api/preparePayload";

interface PersonalInformationProps {
  questionnaireAnswers?: QuestionnaireAnswers;
  landingPageId: string;
  countryCode: string;
  trialIdConfig?: string | TrialIdConfig;
  submitButtonText?: string;
}

export function PersonalInformation({
  questionnaireAnswers = {},
  landingPageId,
  countryCode,
  trialIdConfig,
  submitButtonText = "Submit study application",
}: PersonalInformationProps) {
  const selectedCountry = getCountryByCode(countryCode);

  const {
    isSubmitting,
    isSubmitted,
    showAgeModal,
    isZipCodeValid,
    formattedErrorMessage,
    submissionError,
    handleSubmit: onSubmit,
    handleAgeModalContinue,
    handleAgeModalClose,
    checkZipCodeAndSetEligibility,
  } = useContactForm({
    questionnaireAnswers,
    landingPageId,
    selectedCountry,
  });

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = useForm<ExistingPatientContactFormSchema | NewPatientContactFormSchema>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: zodResolver(existingPatientContactFormSchema),
    defaultValues: {
      country: countryCode,
      zipCode: getContactFormState(landingPageId).zipCode || "",
      firstName: getContactFormState(landingPageId).firstName || "",
      lastName: getContactFormState(landingPageId).lastName || "",
      dateOfBirth: getContactFormState(landingPageId).dateOfBirth || "",
      sex: getContactFormState(landingPageId).sex || undefined,
      ethnicity: getContactFormState(landingPageId).ethnicity || [],
      phone: getContactFormState(landingPageId).phone || "",
      termsAccepted: getContactFormState(landingPageId).termsAccepted ?? false,
      phoneValid: getContactFormState(landingPageId).phoneValid ?? false,
      thirdPartyContactConsentGranted: getContactFormState(landingPageId).thirdPartyContactConsentGranted ?? false,
      thirdPartyContactConsentVersion: getContactFormState(landingPageId).thirdPartyContactConsentVersion || "",
      consentLocaleCode: getContactFormState(landingPageId).consentLocaleCode || "",
      trialSiteId: getContactFormState(landingPageId).trialSiteId || "",
      trialSiteTrialId: getContactFormState(landingPageId).trialSiteTrialId || "",
    },
  });
  const typedErrors = errors as FieldErrors<NewPatientContactFormSchema>;

  const dateOfBirthValue = watch("dateOfBirth");
  const [validatedDateOfBirth, setValidatedDateOfBirth] = useState<
    string | undefined
  >(undefined);

  const postalCodeValue = watch("zipCode");
  const [validatedPostalCode, setValidatedPostalCode] = useState<
    string | undefined
  >(undefined);

  const localeCode = "EN";
  
  const successRef = useRef<HTMLDivElement>(null);
  const errorMessageRef = useRef<HTMLDivElement>(null);

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [thirdPartyConsentCheckboxVisible, setThirdPartyConsentCheckboxVisible] = useState(false);

  const { track } = useAnalytics();
  const trackFieldError = useFieldErrorTracking();

  // Persist cross-page fields
  useEffect(() => {
    const sub = watch((values, info) => {
      const v: any = values;
      switch (info.name) {
        case "zipCode":
        case "firstName":
        case "lastName":
        case "dateOfBirth":
        case "sex":
        case "phone":
        case "phoneValid":
        case "termsAccepted":
        case "thirdPartyContactConsentGranted":
        case "thirdPartyContactConsentVersion":
        case "consentLocaleCode":
        case "trialSiteId":
        case "trialSiteTrialId":
        case "ethnicity":
          setContactFormState(landingPageId, {
            [info.name!]: v?.[info.name!],
          } as any);
          break;
        default:
          break;
      }
    });
    return () => sub.unsubscribe();
  }, [watch, landingPageId]);

  // Handle initial validation of prefilled values
  useEffect(() => {
    const initialZipCode = getContactFormState(landingPageId).zipCode;
    const initialDateOfBirth = getContactFormState(landingPageId).dateOfBirth;
    
    // Trigger zip code validation for initial value if present
    if (initialZipCode && initialZipCode.length === 5) {
      // Don't set validatedPostalCode here - let the existing useEffect handle it
      // when isZipCodeValid becomes true
      checkZipCodeAndSetEligibility(initialZipCode);
    }
    
    // Validate initial date of birth if present
    if (initialDateOfBirth) {
      const formattedDob = formatDobAsMMDDYYYY(initialDateOfBirth);
      if (formattedDob && formattedDob.length === 10) {
        const parsedDate = parse(formattedDob, "MM/dd/yyyy", new Date());
        const isDateValid = isValid(parsedDate) && 
          parsedDate.getFullYear() >= 1900 && 
          parsedDate.getFullYear() <= new Date().getFullYear();
        
        if (isDateValid) {
          setValidatedDateOfBirth(formattedDob);
        }
      }
    }
  }, [landingPageId]);
  
  useEffect(() => {
    track("lp.personal_info.viewed", {
      has_prescreener: Object.keys(questionnaireAnswers?.questionAnswers || {}).length > 0,
      landing_page_id: landingPageId,
    });
  }, []);

  const matchingTrialId: string | undefined = useMemo(() => {
    return (
      trialIdConfig &&
      validatedDateOfBirth &&
      getMatchingTrialId(
        trialIdConfig,
        questionnaireAnswers,
        validatedDateOfBirth,
        landingPageId,
      )
    );
  }, [
    landingPageId,
    trialIdConfig,
    validatedDateOfBirth,
    questionnaireAnswers,
  ]);
  const {
    value: closestTrialSite,
    loading: closestTrialSiteLoading,
    error: closestTrialSiteError,
  } = useNearestTrialSite(matchingTrialId, selectedCountry.code, validatedPostalCode, localeCode);

  function setHiddenFields(trialSiteWithConsent: TrialSiteWithConsent | undefined) {
    if (trialSiteWithConsent) {
      setValue("trialSiteId", String(trialSiteWithConsent.id), { shouldDirty: true });
      setValue("trialSiteTrialId", trialSiteWithConsent.trial.id, { shouldDirty: true });
      setValue("consentLocaleCode", trialSiteWithConsent.consent.localeCode, { shouldDirty: true });
      setValue("thirdPartyContactConsentVersion", String(trialSiteWithConsent.consent.template.version), { shouldDirty: true });
    } else {
      setValue("trialSiteId", "", { shouldDirty: true });
      setValue("trialSiteTrialId", "", { shouldDirty: true });
      setValue("consentLocaleCode", "", { shouldDirty: true });
      setValue("thirdPartyContactConsentVersion", "", { shouldDirty: true });
    }
  }
  useEffect(() => {
    if (postalCodeValue && isZipCodeValid) {
      setValidatedPostalCode(postalCodeValue);
    }
  }, [postalCodeValue, isZipCodeValid]);

  useEffect(() => {
    if (closestTrialSiteLoading) {
      setSubmitEnabled(false);
      setThirdPartyConsentCheckboxVisible(false);
      setHiddenFields(undefined);
    } else if (closestTrialSiteError) {
      setThirdPartyConsentCheckboxVisible(false);
      setHiddenFields(undefined);
      setSubmitEnabled(true);
    } else if (closestTrialSite) {
      setSubmitEnabled(true);
      setThirdPartyConsentCheckboxVisible(true);
      setHiddenFields(closestTrialSite);
    } else {
      setSubmitEnabled(true);
      setHiddenFields(undefined);
    }
  }, [closestTrialSite, closestTrialSiteLoading, closestTrialSiteError]);

  useEffect(() => {
    if (formattedErrorMessage && errorMessageRef.current) {
      setTimeout(() => {
        errorMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [formattedErrorMessage]);

  useEffect(() => {
    if (isSubmitted && successRef.current) {
      setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [isSubmitted]);

  function handlePhoneValidationChange(isValid: boolean) {
    setIsPhoneValid(isValid);
    // mark dirty to persist; full validation occurs on blur per mode
    setValue("phoneValid", isValid, { shouldDirty: true });
  }

  function handleDateOfBirthChange(
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) {
    let value = e.target.value.replace(/[^\d/]/g, "");
    if (value.length === 2 && !value.includes("/")) value = value + "/";
    else if (value.length === 5 && value.split("/").length === 2) value = value + "/";

    if (value.length <= 10) {
      onChange(value);
      if (value.length === 10) {
        const parsedDate = parse(value, "MM/dd/yyyy", new Date());
        const isDateValid =
          isValid(parsedDate) &&
          parsedDate.getFullYear() >= 1900 &&
          parsedDate.getFullYear() <= new Date().getFullYear();
        if (isDateValid) {          
          setValidatedDateOfBirth(value);
        } else {
          trackFieldError("dateOfBirth", "invalid"); 
        }
      }
    }
  }

  function formatDobAsMMDDYYYY(dobIsoOrYYYYMMDD: string): string {
    // Handles incoming formats like YYYY-MM-DD or ISO strings
    const date = new Date(dobIsoOrYYYYMMDD);
    if (Number.isNaN(date.getTime())) return "";
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  function handleFormSubmit(data: ContactFormSchema) {
    track("lp.personal_info.submit_clicked", {
      form_complete: Object.values(data).every((v) => v !== "" && v !== false),
      has_questionnaire_answers: Object.keys(questionnaireAnswers).length > 0,
      landing_page_id: landingPageId,
    });
    onSubmit(data);
  }

  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100">
      <div className="p-6">
        {formattedErrorMessage && (
          <div
            ref={errorMessageRef}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-start animate-fade-in"
            data-test-id="personal-form-error"
          >
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Form submission failed</p>
              <p className="text-sm mt-1">{formattedErrorMessage}</p>
              {submissionError?.name === "ApiError" && submissionError.response && "status" in submissionError.response && (
                <p className="text-xs mt-2">Error code: {typeof submissionError.response.status === "number" ? submissionError.response.status : "Unknown"}</p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8" data-test-id="personal-information-form">          
          <div className="space-y-6 bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-test-id="personal-info-section">
            <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
              <User className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
            </div>
            <div className="space-y-6">

            <div className="mb-6">
                <label htmlFor="zipCode" className="block text-gray-700 font-medium mb-2">
                  {selectedCountry.postalCodeLabel} <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-4 w-4 text-gray-400" />
                  </div>
                  <RHController
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="tel"
                        inputMode="numeric"
                        id="zipCode"
                        data-test-id="input-zipcode"
                        value={field.value}
                        onChange={(e) => {
                          const formatted = formatPostalCode(selectedCountry, e.target.value);
                          field.onChange(formatted);
                          if (formatted.length === 5) {
                            checkZipCodeAndSetEligibility(formatted);
                          }
                        }}
                        onBlur={field.onBlur}
                        placeholder={selectedCountry.postalCodeExample}
                        className={`block w-48 pl-10 border ${errors.zipCode ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"} rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm transition-colors duration-200`}
                      />
                    )}
                  />
                </div>
                {errors.zipCode && (
                  <div className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.zipCode.message}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                  Date of birth <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative rounded-md shadow-sm inline-block max-w-xs">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="text"
                        id="dateOfBirth"
                        data-test-id="input-dateOfBirth"
                        inputMode="numeric"
                        placeholder="MM/DD/YYYY"
                        className={`block w-full pl-10 border ${
                          typedErrors.dateOfBirth
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm transition-colors duration-200`}
                        value={field.value}
                        onChange={(e) => handleDateOfBirthChange(e, field.onChange)}
                        onBlur={(e) => {
                          field.onBlur();
                          const value = e.target.value;
                          if (value && value.length !== 10) {
                            trackFieldError("dateOfBirth", "incomplete");
                            if (!typedErrors.dateOfBirth) {
                              setValue("dateOfBirth", value, { shouldValidate: true, shouldDirty: true });
                            }
                            return;
                          }
                          if (value) {
                            const parsedDate = parse(value, "MM/dd/yyyy", new Date());
                            const isDateValid =
                              isValid(parsedDate) &&
                              parsedDate.getFullYear() >= 1900 &&
                              parsedDate.getFullYear() <= new Date().getFullYear();
                            if (!isDateValid) {
                              trackFieldError("dateOfBirth", "invalid");
                              setValue("dateOfBirth", value, { shouldValidate: true, shouldDirty: true });
                            }
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col justify-start">
                  {typedErrors.dateOfBirth ? (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {typedErrors.dateOfBirth.message || "Please enter a valid date (MM/DD/YYYY)"}
                    </div>
                  ) : (
                    <>
                      <div className="text-xs mt-1 text-gray-500">Format: MM/DD/YYYY</div>
                      {dateOfBirthValue ? (
                        <div className={`text-xs mt-1 ${isUnderEighteen(dateOfBirthValue) ? "text-amber-600" : "text-green-600"}`}>
                          {fixDateFormatting(dateOfBirthValue) || (isUnderEighteen(dateOfBirthValue) ? "Under 18 years old - May need parental consent" : "")}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="firstName"
                        data-test-id="input-firstName"
                        {...register("firstName")}
                        className={`block w-full pl-10 border ${
                          typedErrors.firstName
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm transition-colors duration-200`}
                      />
                    </div>
                    {typedErrors.firstName && (
                      <div className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {typedErrors.firstName.message}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="lastName"
                        data-test-id="input-lastName"
                        {...register("lastName")}
                        className={`block w-full pl-10 border ${
                          typedErrors.lastName
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm transition-colors duration-200`}
                      />
                    </div>
                    {typedErrors.lastName && (
                      <div className="text-red-500 text-sm mt-1 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {typedErrors.lastName.message}
                      </div>
                    )}
                  </div>
                </div>

                <div data-test-id="sex-options">
                  <label className="block text-sm font-medium text-gray-700">
                    Biological sex <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex space-x-6">
                    <div className="flex items-center">
                      <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="sex-male"
                            data-test-id="input-sex-male"
                            type="radio"
                            value="M"
                            checked={field.value === "M"}
                            onChange={() => field.onChange("M")}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        )}
                      />
                      <label htmlFor="sex-male" className="ml-2 block text-sm text-gray-700">
                        Male
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="sex-female"
                            data-test-id="input-sex-female"
                            type="radio"
                            value="F"
                            checked={field.value === "F"}
                            onChange={() => field.onChange("F")}
                            className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        )}
                      />
                      <label htmlFor="sex-female" className="ml-2 block text-sm text-gray-700">
                        Female
                      </label>
                    </div>
                  </div>
                  {typedErrors.sex && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {typedErrors.sex.message}
                    </div>
                  )}
                </div>

              {countryCode === "US" ? (
                <EthnicitySelector control={control} errors={typedErrors} />
              ) : (
                <CanadianEthnicitySelector control={control} errors={typedErrors} />
              )}

              <div>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneNumberInput
                      id="phone"
                      label="Phone"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => { field.onBlur(); setIsPhoneFocused(false); }}
                      onFocus={() => setIsPhoneFocused(true)}
                      onValidationChange={handlePhoneValidationChange}
                      name={field.name}
                      selectedCountry={selectedCountry}
                    />
                  )}
                />
                {typedErrors.phone && !isPhoneFocused && (
                  <div className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {typedErrors.phone?.message}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Site consent controls (render when available) */}
          {thirdPartyConsentCheckboxVisible && closestTrialSite && (
            <div className="relative flex flex-col items-start p-4 rounded-lg bg-gray-50">
              <div className="text-sm leading-6">{closestTrialSite.consent.preamble}</div>
              <div className="text-xs leading-6 mt-4">
                <ConsentBodyText
                  text={closestTrialSite.consent.consent}
                  sites={closestTrialSite.consent.sites}
                />
              </div>
              <div className="flex flex-row mt-6 items-center">
                <div className="flex h-5 items-center">
                  <Controller
                    name="thirdPartyContactConsentGranted"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="thirdPartyContactConsentGranted"
                        data-test-id="input-thirdPartyContactConsentGranted"
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    )}
                  />
                </div>
                <div className="ml-3 text-sm leading-6 flex-1">
                  <label htmlFor="thirdPartyContactConsentGranted" className="font-medium text-gray-700">
                    {closestTrialSite.consent.confirmedButtonText}
                  </label>
                </div>
              </div>
              {typedErrors.thirdPartyContactConsentGranted && (
                <div className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {typedErrors.thirdPartyContactConsentGranted.message}
                </div>
              )}
            </div>
          )}

          <TermsAcceptance countryCode={countryCode} control={control} errors={errors} handleInputFocus={() => {}} />

          {/* Hidden Fields */}
          <input
            id="phoneValid"
            type="hidden"
            {...register("phoneValid")}
          />
          <input
            id="trialSiteId"
            type="text"
            className="hidden"
            {...register("trialSiteId")}
            value={""}
          />
          <input
            id="trialSiteTrialId"
            type="text"
            className="hidden"
            {...register("trialSiteTrialId")}
            value={""}
          />
          <input
            id="consentLocaleCode"
            type="text"
            className="hidden"
            {...register("consentLocaleCode")}
            value={""}
          />
          <input
            id="thirdPartyContactConsentVersion"
            type="text"
            className="hidden"
            {...register("thirdPartyContactConsentVersion")}
            value={""}
          />

          <div className="pt-4">
            <button
              type="submit"
              data-test-id="personal-information-submit"
              disabled={isSubmitting || !submitEnabled || !isPhoneValid}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white transition-all duration-200 ${
                isSubmitting || !submitEnabled || !isPhoneValid
                  ? "bg-gray-400 cursor-not-allowed opacity-60"
                  : formattedErrorMessage 
                    ? "bg-red-600 hover:bg-red-700 focus:ring-red-500" 
                    : "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isSubmitting || !submitEnabled || !isPhoneValid
                  ? "focus:ring-gray-400"
                  : ""
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </div>
              ) : formattedErrorMessage ? (
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Try again
                </div>
              ) : (
                submitButtonText
              )}
            </button>
          </div>
        </form>

        <Modal isOpen={showAgeModal} onClose={handleAgeModalClose} title="Eligibility Information" data-test-id="age-modal">
          <div className="flex items-start mb-4">
            <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
              <AlertCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-gray-800 font-medium mb-2">This clinical trial is only open to individuals who are 18 years of age or older.</p>
              <p className="text-gray-600 mb-4">We'll redirect you to a questionnaire designed for your age group, where you can explore other potential research opportunities that may be better suited for you.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button type="button" data-test-id="age-modal-back" onClick={handleAgeModalClose} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Go Back
            </button>
            <button type="button" data-test-id="age-modal-continue" onClick={handleAgeModalContinue} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              Find Other Trials
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
